import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { FormikProps, Formik, Field, Form } from "formik";
import * as yup from "yup";
import { request } from "../../../../utils/Request";
import { Col, Row, FormGroup } from "react-bootstrap";
import { InputField } from "../../../../components/Shared/InputField";
import { Sidebar } from "primereact/sidebar";
import { get, includes, findIndex } from "lodash";
import { Button } from "primereact/button";
import { css, cx } from "@emotion/css";

const STANDARD_PERMISSION_ACTIONS = [
    { key: 'view', label: 'view', suffix: 'view' },
    { key: 'store', label: 'create', suffix: 'store' },
    { key: 'update', label: 'update', suffix: 'update' },
    { key: 'destroy', label: 'delete', suffix: 'destroy' },
];

const permissionMatrixStyles = css`
    border: 1px solid var(--surface-border);
    border-radius: 12px;
    overflow: hidden;
    background: var(--surface-card);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

    .permission-matrix-table {
        border-collapse: separate;
        border-spacing: 0;
        margin-bottom: 0;
    }

    .permission-matrix-table th {
        padding: 0.85rem 0.75rem;
        border: 0;
        border-bottom: 1px solid var(--surface-border);
        background: var(--surface-ground);
        color: var(--text-color-secondary);
        font-size: 0.76rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        white-space: nowrap;
    }

    .permission-matrix-table td {
        padding: 0.75rem;
        border: 0;
        border-bottom: 1px solid var(--surface-border);
        vertical-align: middle;
    }

    .permission-matrix-table tbody tr:last-child td {
        border-bottom: 0;
    }

    .permission-matrix-table tbody tr:hover {
        background: var(--surface-hover);
    }

    .permission-module-cell {
        min-width: 220px;
        background: rgba(0, 0, 0, 0.015);
    }

    .permission-action-cell {
        width: 96px;
        min-width: 96px;
        text-align: center;
    }

    .permission-extra-cell {
        min-width: 260px;
    }

    .permission-module-label {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0;
        font-weight: 700;
        color: var(--text-color);
    }

    .permission-extra-list {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    .permission-extra-item {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        margin: 0;
        padding: 0.35rem 0.55rem;
        border: 1px solid var(--surface-border);
        border-radius: 999px;
        background: var(--surface-ground);
        font-size: 0.82rem;
        line-height: 1.2;
    }

    .permission-empty {
        display: inline-flex;
        width: 18px;
        height: 2px;
        border-radius: 999px;
        background: var(--surface-border);
    }

    .form-check-input {
        cursor: pointer;
    }
`;

interface IFormProps {
    t?: any | undefined;
    moduleState: any;
    setQuery: any;
    dropdowns: any;
    params: any;
    toastify: any;
    onClose?: any;
    operationCancel: any;
}

type TInputForm = {
    id?: any,
    name?: string | null,
    update_permission_with_master_role?: boolean,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        name: yup.string().trim(t('the field is invalid.')).required(t('the field is required.', { field: t('title') })).nullable(),
    });
};

const DEFAULT_FORM_STATE = {
    id: null,
    name: null,
    update_permission_with_master_role: false,
};

const resolvePermissionAction = (slug: string): string | null => {
    const action = STANDARD_PERMISSION_ACTIONS.find((item) => slug.endsWith(item.suffix));

    return action?.key ?? null;
};

const groupPermissionsByModule = (permissions: any[]) => {
    return permissions.reduce((carry: any[], permission: any) => {
        const moduleName = permission.description || 'other';
        let group = carry.find((item: any) => item.name === moduleName);

        if ( !group ) {
            group = {
                name: moduleName,
                actions: {},
                extra: [],
                permissions: [],
            };
            carry.push(group);
        }

        const action = resolvePermissionAction(permission.slug);
        group.permissions.push(permission);

        if ( action && !group.actions[action] ) {
            group.actions[action] = permission;
        } else {
            group.extra.push(permission);
        }

        return carry;
    }, []);
};

function RoleForm(props: IFormProps) {
    const { t, toastify, params, dropdowns, operationCancel } = props;
    const formRef: any = useRef();
    const [initFormState, setInitFormState] = useState<TInputForm>(DEFAULT_FORM_STATE);
    const [panelState, setPanelState] = useState<boolean>(false);
    const [permissions, setPermissions] = useState(dropdowns.permissions.map((i: any) => ( { ...i, checked: false } )));
    const [checkedPermissions, setCheckedPermissions] = useState(new Array(dropdowns.permissions.length).fill(false));
    const permissionGroups = groupPermissionsByModule(permissions);

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        try {
            const response = await request({
                method: ( !payloads.id ? 'POST' : 'PATCH' ),
                url: ( !payloads.id ? `/suppliers/roles` : `/suppliers/roles/${ payloads.id }` ),
                data: {
                    ...payloads,
                    update_permission_with_master_role: Boolean(payloads.update_permission_with_master_role),
                    permissions: permissions.filter((item: any) => item.checked).map((item: any) => item.slug)
                },
            });

            if ( payloads.id ) {
                toastify(t("record updated", { item: t('role') }), "success");
            } else {
                toastify(t("record added", { item: t('role') }), "info");
            }

            resetForm();
            onClose();
        } catch (e: any) {
            if ( e.status === 422 ) {
                setErrors(e.data.errors);
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const onReset = () => {
        const base: any = formRef.current;

        if ( base ) {
            base.resetForm();
        }
    };

    const onClose = () => {
        setPanelState(false);
        const base: any = formRef.current;

        if ( base ) {
            base.resetForm();
        }

        if ( props.onClose ) {
            props.onClose(
                !includes(['edit'], get(params, ['operation']))
            );
        }
    };

    const handleOnChange = (item: any, status: boolean) => {
        let shallowPermissions: any = [...permissions];
        const index = findIndex(shallowPermissions, (loop: any) => loop.slug === item.slug);
        let newItem: any = { ...shallowPermissions[index], checked: undefined };
        newItem.checked = status;
        shallowPermissions[index] = newItem;
        setPermissions(shallowPermissions);

        const updatedCheckedState = checkedPermissions.map((item, cIndex) => ( cIndex === index ? status : item ));
        setCheckedPermissions(updatedCheckedState);
    };

    const handleOnChangeAllModulePermissions = (group: any, status: boolean) => {
        const groupSlugs = group.permissions.map((item: any) => item.slug);
        const updatedPermissions = permissions.map((permission: any) => (
            groupSlugs.includes(permission.slug) ? { ...permission, checked: status } : permission
        ));
        const updatedCheckedState = updatedPermissions.map((permission: any) => Boolean(permission.checked));

        setPermissions(updatedPermissions);
        setCheckedPermissions(updatedCheckedState);
    };

    const onEdit = async (id: string): Promise<void> => {
        try {
            const response = await request({
                url: `/suppliers/roles/${ id }`
            });

            const { data } = response.data;
            const formData: TInputForm = { ...data };
            if ( data.permissions.length > 0 ) {
                const selectedPermissionSlugs = data.permissions.map((item: any) => item.slug);
                const updatedPermissions = permissions.map((item: any) => ({
                    ...item,
                    checked: selectedPermissionSlugs.includes(item.slug),
                }));

                setPermissions(updatedPermissions);
                setCheckedPermissions(updatedPermissions.map((item: any) => Boolean(item.checked)));
            }
            setInitFormState(formData);
            setPanelState(true);
        } catch (error: any) {
            toastify(t("server error"), "error");
        }
    };

    useEffect(() => {
        // setInitFormState(DEFAULT_FORM_STATE);

        if ( includes(['add'], get(params, ['operation'])) ) {
            setPanelState(true);
        }

        if ( includes(['edit'], get(params, ['operation'])) ) {
            onEdit(params.operationId);
        }
    }, [params]);

    return (
        <>
            <div>
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('role') + ' ' + t('action') }</h5>
                            <p>{ t("using this form you can add or update the record") }</p>
                        </div>
                    }
                    visible={ panelState } position="right" onHide={ onClose } blockScroll={ true }
                    style={ { width: '1100px', maxWidth: '95vw' } } closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>
                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 md:mb-7 mb-7 sm:mb-7" }>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="name"
                                                   label={ t("title") }
                                                   placeholder={ t("enter") + ' ' + t("title").toLowerCase() }
                                                   isRequired
                                                   autoFocus />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <FormGroup className={ "mt-2" }>
                                                <label className={ "form-check-label" }>
                                                    <input
                                                        type="checkbox"
                                                        className="me-2 form-check-input"
                                                        checked={ Boolean(props.values.update_permission_with_master_role) }
                                                        onChange={ (e) => props.setFieldValue('update_permission_with_master_role', e.target.checked) }
                                                    />
                                                    { t("update permission with master role") }
                                                </label>
                                            </FormGroup>
                                        </Col>
                                        <Col sm={ 12 } md={ 12 }>
                                            <div className="col-sm-12">
                                                <fieldset>
                                                    <h4 className={ 'p-l-5 p-r-5 text-capitalize' }>{ t('permissions') }:</h4>
                                                    <div className={ cx("table-responsive", permissionMatrixStyles) }>
                                                        <table className={ "table align-middle permission-matrix-table" }>
                                                            <thead>
                                                                <tr>
                                                                    <th className={ "text-capitalize" }>{ t('module') }</th>
                                                                    {
                                                                        STANDARD_PERMISSION_ACTIONS.map((action) => (
                                                                            <th key={ action.key } className={ "permission-action-cell text-capitalize" }>
                                                                                { t(action.label) }
                                                                            </th>
                                                                        ))
                                                                    }
                                                                    <th className={ "permission-extra-cell text-capitalize" }>{ t('extra') }</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {
                                                                    permissionGroups.map((group: any) => {
                                                                        const allChecked = group.permissions.every((permission: any) => Boolean(permission.checked));

                                                                        return (
                                                                            <tr key={ group.name }>
                                                                                <td className={ "permission-module-cell" }>
                                                                                    <label className={ "form-check-label text-capitalize permission-module-label" }>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            className="form-check-input"
                                                                                            checked={ allChecked }
                                                                                            onChange={ (e) => handleOnChangeAllModulePermissions(group, e.target.checked) }
                                                                                        />
                                                                                        { t(group.name) }
                                                                                    </label>
                                                                                </td>
                                                                                {
                                                                                    STANDARD_PERMISSION_ACTIONS.map((action) => {
                                                                                        const permission = group.actions[action.key];
                                                                                        const index = permission ? findIndex(permissions, (item: any) => item.slug === permission.slug) : -1;

                                                                                        return (
                                                                                            <td key={ action.key } className={ "permission-action-cell" }>
                                                                                                {
                                                                                                    permission ?
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        className="form-check-input"
                                                                                                        onChange={ (e) => handleOnChange(permission, e.target.checked) }
                                                                                                        checked={ checkedPermissions[index] }
                                                                                                        title={ permission.name }
                                                                                                    />
                                                                                                    : <span className={ "permission-empty" } />
                                                                                                }
                                                                                            </td>
                                                                                        );
                                                                                    })
                                                                                }
                                                                                <td className={ "permission-extra-cell" }>
                                                                                    {
                                                                                        group.extra.length > 0
                                                                                            ? <div className={ "permission-extra-list" }>
                                                                                                {
                                                                                                    group.extra.map((permission: any) => {
                                                                                                        const index = findIndex(permissions, (item: any) => item.slug === permission.slug);

                                                                                                        return (
                                                                                                            <label key={ permission.slug } className={ "form-check-label permission-extra-item" }>
                                                                                                                <input
                                                                                                                    type="checkbox"
                                                                                                                    className="form-check-input"
                                                                                                                    onChange={ (e) => handleOnChange(permission, e.target.checked) }
                                                                                                                    checked={ checkedPermissions[index] }
                                                                                                                />
                                                                                                                <span>{ permission.name }</span>
                                                                                                            </label>
                                                                                                        );
                                                                                                    })
                                                                                                }
                                                                                            </div>
                                                                                            : <span className={ "text-muted" }>-</span>
                                                                                    }
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })
                                                                }
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </fieldset>
                                            </div>
                                        </Col>
                                    </Row>
                                    <div className="filter-card-footer"
                                         style={ { width: '1085px', maxWidth: 'calc(95vw - 15px)' } }>
                                        <Button type={ "submit" } disabled={ props.isSubmitting }
                                                tooltip={ t("to data save into database") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-save me-2" />
                                            { t("let's save") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                onClick={ onReset }
                                                tooltip={ t("to make all fields empty") } className={ "ms-2" }
                                                size={ "small" } severity={ "warning" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                        >
                                            <i className="pi pi-refresh me-2" />
                                            { t("reset") }
                                        </Button>
                                        <Button type={ "button" } disabled={ props.isSubmitting }
                                                tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                                onClick={() => operationCancel(null, null)}
                                        >
                                            <i className="pi pi-arrow-left me-2" />
                                            { t("cancel") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            </div>
        </>
    );
}

RoleForm.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(RoleForm);
